import {
	BigNum,
	isVariant,
	MarketType,
	PositionDirection,
} from '@drift-labs/sdk';
import {
	COMMON_UI_UTILS,
	ENUM_UTILS,
	MarketId,
	matchEnum,
	UIMarket,
	UISerializableOrder,
} from '@drift/common';
import { PencilIcon } from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import { useCallback, useEffect, useState } from 'react';
import { useTickedAccountData } from 'src/hooks/useAccountData';
import useDriftActions from 'src/hooks/useDriftActions';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { LPOpenOrderData } from 'src/hooks/useOpenOrdersData';
import useWindowSizeString from 'src/hooks/useWindowSize';
import useDevSwitchIsOn from '../hooks/useDevSwitchIsOn';
import useShowAccountValues from '../hooks/useShowAccountValues';
import useWalletIsConnected from '../hooks/useWalletIsConnected';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import Button from './Button';
import TableActionButton from './Buttons/TableActionButton';
import EditOrderPopup from './Popups/EditOrderPopup';
import SecondaryConnectWalletButton from './SecondaryConnectWalletButton';
import TableV2 from './Tables/TableV2';
import TableStateWrapper from './TableStateWrapper';
import Text from './Text/Text';
import Tooltip from './Tooltip/Tooltip';
import CheckboxInput from './CheckboxInput';
import useMarketStateStore from 'src/stores/useMarketStateStore';

export type AccountOrder = UISerializableOrder & { accountName?: string };
export type AccountLPOrder = LPOpenOrderData & { accountName?: string };

const EMPTY_ORDERS_STATE = [];

const OpenOrdersTable = (props: { overviewPage?: boolean }) => {
	const setStore = useDriftStore((s) => s.set);
	const showOnlyCurrentMarket =
		useDriftStore((s) => s.showOnlyCurrentMarket) && !props.overviewPage;
	const showFillButton = useDevSwitchIsOn();
	const tableGrid = `minmax(130px,10fr) minmax(85px,11fr) minmax(120px,11fr) minmax(120px,11fr) minmax(100px,8fr) minmax(100px,7fr) ${
		showFillButton ? 'minmax(85px,5fr)' : ''
	}`;
	const connected = useWalletIsConnected();
	const windowSize = useWindowSizeString();
	const currentAccount = useTickedAccountData();
	const actions = useDriftActions();
	const isMobile = useIsMobileScreenSize();
	const showAccountValues = useShowAccountValues();
	const [showEditOrderPopup, setShowEditOrderPopup] = useState(false);
	const [orderToEdit, setOrderToEdit] = useState<AccountOrder>();
	const [editOrderTargetElementId, setEditOrderTargetElementId] = useState('');

	const getOraclePrice = useMarketStateStore((s) => s.getOraclePriceForMarket);

	// Filter orders and map with the order index to be used in cancelling the order
	const openOrders: AccountOrder[] =
		currentAccount?.openOrders ?? EMPTY_ORDERS_STATE;
	const openLpOrders: AccountLPOrder[] =
		currentAccount?.openLpOrders ?? EMPTY_ORDERS_STATE;

	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);

	const [displayOrders, setDisplayOrders] = useState<AccountOrder[]>(undefined);
	const [filteredDisplayOrders, setFilteredDisplayOrders] =
		useState<AccountOrder[]>(undefined);
	const [displayLpOrders, setDisplayLpOrders] =
		useState<AccountLPOrder[]>(undefined);
	const [filteredDisplayLpOrders, setFilteredDisplayLpOrders] =
		useState<AccountLPOrder[]>(undefined);

	useEffect(() => {
		if (!openOrders) return;

		let tempOrders = openOrders;

		const ordersForMarket = openOrders.filter(
			(order) =>
				order.marketIndex === selectedMarket.market.marketIndex &&
				ENUM_UTILS.toStr(order.marketType) == selectedMarket.marketType
		);

		if (ordersForMarket.length > 0) {
			tempOrders = ordersForMarket.concat(
				showOnlyCurrentMarket
					? []
					: openOrders.filter(
							(order) =>
								order.marketIndex !== selectedMarket.market.marketIndex ||
								ENUM_UTILS.toStr(order.marketType) !== selectedMarket.marketType
					  )
			);
		}

		const displayOrders = [...tempOrders].sort(
			(orderA, orderB) => orderB.slot.toNumber() - orderA.slot.toNumber()
		);

		setDisplayOrders(displayOrders);
		setFilteredDisplayOrders(
			displayOrders.filter((order) =>
				showOnlyCurrentMarket
					? order.marketIndex === selectedMarket.market.marketIndex
					: true
			)
		);
	}, [openOrders, selectedMarket?.market?.symbol, showOnlyCurrentMarket]);

	useEffect(() => {
		if (!openLpOrders) return;

		let tempLpOrders = openLpOrders;

		const lpOrdersForMarket = openLpOrders.filter(
			(order) =>
				order.marketIndex === selectedMarket.market.marketIndex &&
				ENUM_UTILS.match(selectedMarket.marketType, MarketType.PERP)
		);

		if (lpOrdersForMarket.length > 0) {
			tempLpOrders = lpOrdersForMarket.concat(
				openLpOrders.filter(
					(order) => order.marketIndex !== selectedMarket.market.marketIndex
				)
			);
		}

		setDisplayLpOrders(tempLpOrders);
		setFilteredDisplayLpOrders(
			tempLpOrders.filter((order) =>
				showOnlyCurrentMarket
					? order.marketIndex === selectedMarket.market.marketIndex
					: true
			)
		);
	}, [openLpOrders, selectedMarket?.market?.symbol, showOnlyCurrentMarket]);

	const headers = [
		'market',
		'type',
		'size',
		'trigger / limit',
		'flags',
		'action',
	];

	const handleEdit = (order: AccountOrder, targetElementId) => {
		setShowEditOrderPopup(true);
		setEditOrderTargetElementId(targetElementId);
		setOrderToEdit(order);
	};

	const handleToggleShowOnlyCurrentMarket = () => {
		setStore((s) => {
			s.showOnlyCurrentMarket = !s.showOnlyCurrentMarket;
		});
	};

	const handleCancelAllOrCurrentOrders = useCallback(() => {
		actions.cancelAllOrders({
			selectedMarketOnly: showOnlyCurrentMarket,
		});
	}, [showOnlyCurrentMarket]);

	const showLpModal = useCallback(
		(
			marketIndex: number,
			modalStr: 'showAddLiquidityModal' | 'showRemoveLiquidityModal'
		) => {
			actions.switchMarket({
				marketIndex,
				marketType: MarketType.PERP,
			});
			actions.showModal(modalStr);
		},
		[]
	);

	const handleClickMarket = useCallback(
		(marketIndex: number, marketType: MarketType) => {
			actions.switchMarket({
				marketIndex,
				marketType,
				goToTradePage: true,
			});
		},
		[]
	);

	const getOrderLimitPrice = (order: AccountOrder): BigNum => {
		const marketId = isVariant(order.marketType, 'perp')
			? MarketId.createPerpMarket(order.marketIndex)
			: MarketId.createSpotMarket(order.marketIndex);

		return COMMON_UI_UTILS.getLimitPriceFromOracleOffset(
			order,
			getOraclePrice(marketId)
		);
	};

	const tableStateRecords =
		!displayOrders && !displayLpOrders
			? undefined
			: (displayOrders ?? ([] as any[])).concat(
					displayLpOrders ?? ([] as any[])
			  );

	if (windowSize === 'xs') {
		return (
			<div className={`flex flex-col text-text-emphasis`}>
				{!tableStateRecords || tableStateRecords.length === 0 ? (
					<div
						className={`flex flex-col justify-center items-center sm:h-full w-full text-center rounded-md`}
					>
						{connected ? (
							<div className="my-8">
								<Text.BODY2 className="capitalize text-text-emphasis">
									No open orders found
								</Text.BODY2>
							</div>
						) : (
							<div className="sm:pt-8">
								<SecondaryConnectWalletButton />
							</div>
						)}
					</div>
				) : (
					<>
						<div className="space-y-2 overflow-y-scroll divide-y thin-scroll divide-container-border">
							{filteredDisplayOrders.map((order) => (
								<TableV2.OrderTableCard
									onClose={() => {
										actions.cancelOrder(order.orderId);
									}}
									order={order}
									key={order.orderId.toString()}
								>
									{order.baseAssetAmount.prettyPrint()}
								</TableV2.OrderTableCard>
							))}
							{filteredDisplayLpOrders.map((order, index) => (
								<TableV2.LpOrderTableCard
									action={() => {
										showLpModal(order.marketIndex, 'showAddLiquidityModal');
									}}
									key={`lp_order_${index}`}
									marketIndex={order.marketIndex}
									sizeString={`${
										!showAccountValues
											? `***** LONG / ***** SHORT`
											: `${order.baseAmountLong
													.abs()
													.printShort(true)} LONG / ${order.baseAmountShort
													.abs()
													.printShort(true)} SHORT`
									}`}
								/>
							))}
						</div>
						{filteredDisplayOrders.length > 1 && (
							<div
								className={`w-full p-4 border-y border-container-border text-right`}
							>
								<Button.Secondary
									size="MEDIUM"
									disabled={
										!filteredDisplayOrders || !filteredDisplayOrders.length
									}
									onClick={handleCancelAllOrCurrentOrders}
									negativeRed
									w-full
								>
									{`Cancel ${filteredDisplayOrders?.length || 0} Orders`}
								</Button.Secondary>
							</div>
						)}
					</>
				)}
			</div>
		);
	}

	return (
		<>
			<TableStateWrapper
				requireAccountCreated
				records={tableStateRecords}
				emptyStateText={'No open orders found'}
				requireWalletConnect
				id="open_orders_table"
			>
				<TableV2.Skeleton
					noBorder
					top={
						isMobile ? null : (
							<TableV2.HeaderRow
								grid={tableGrid}
								forceBottomBorder
								header
								className="pr-2"
							>
								{headers.map((label) => (
									<TableV2.HeaderCell
										className={`${
											props.overviewPage ? 'bg-main-bg' : ''
										} capitalize`}
										key={`header_${label}`}
									>
										{label}
									</TableV2.HeaderCell>
								))}
							</TableV2.HeaderRow>
						)
					}
					middle={
						<div className={`sm:max-h-full w-full text-xs`}>
							{filteredDisplayOrders?.map((order, index) => {
								const marketSymbol = new UIMarket(
									order.marketIndex,
									order.marketType
								).marketName;

								const orderLimitPrice = getOrderLimitPrice(order);

								return (
									<TableV2.BodyRow
										key={order.orderId.toString()}
										grid={tableGrid}
										isDataRow
									>
										<TableV2.MarketCell
											key={`market_${index}`}
											className="font-numeral"
											marketSymbol={marketSymbol}
											onClick={() =>
												handleClickMarket(order.marketIndex, order.marketType)
											}
											direction={
												matchEnum(order.marketType, MarketType.PERP)
													? matchEnum(order.direction, PositionDirection.LONG)
														? 'long'
														: 'short'
													: matchEnum(order.direction, PositionDirection.LONG)
													? 'buy'
													: 'sell'
											}
										/>

										<TableV2.BodyCell className="uppercase font-numeral">
											{COMMON_UI_UTILS.getOrderLabelFromOrderDetails(order)}
										</TableV2.BodyCell>

										<TableV2.BodyCell className="uppercase font-numeral">
											{showAccountValues
												? COMMON_UI_UTILS.trimTrailingZeros(
														order.baseAssetAmountFilled.toTradePrecision()
												  )
												: '∗∗∗∗∗'}{' '}
											/{' '}
											{showAccountValues
												? COMMON_UI_UTILS.trimTrailingZeros(
														order.baseAssetAmount.toTradePrecision()
												  )
												: '∗∗∗∗∗'}
										</TableV2.BodyCell>

										<TableV2.BodyCell className="text-left uppercase font-numeral">
											<div>
												{order.triggerPrice.eqZero()
													? '-'
													: order.triggerPrice.toNotional(true)}{' '}
												<span className="text-text-secondary">/</span>{' '}
												{order.oraclePriceOffset ? (
													<Tooltip
														content={
															<span>
																{`Oracle ${
																	order.oraclePriceOffset.gtZero() ? '+' : ''
																}${order.oraclePriceOffset.print()}`}
															</span>
														}
													>
														{orderLimitPrice.toNotional(true)}
													</Tooltip>
												) : (
													<span>
														{orderLimitPrice.eqZero()
															? '-'
															: orderLimitPrice.toNotional(true)}
													</span>
												)}
											</div>
										</TableV2.BodyCell>

										<TableV2.BodyCell className="uppercase font-numeral">
											<div className="flex flex-col w-full gap-1">
												{order.reduceOnly && (
													<Text.MICRO1>Reduce Only</Text.MICRO1>
												)}
												{order.postOnly && <Text.MICRO1>Post Only</Text.MICRO1>}
												{!order.reduceOnly && !order.postOnly && (
													<TableV2.EmptyValueFiller />
												)}
											</div>
										</TableV2.BodyCell>

										<TableV2.BodyCell>
											<div className="flex flex-row">
												<Button.Secondary
													className="justify-start mr-2"
													size="SMALL"
													id={`order_edit_button_${order.orderId.toString()}`}
													onClick={() =>
														handleEdit(
															order,
															`order_edit_button_${order.orderId.toString()}`
														)
													}
												>
													<PencilIcon width={'16px'} />
												</Button.Secondary>
												<Button.Secondary
													size="SMALL"
													className={`flex items-center justify-between border-none px-1 py-1 font-display`}
													negativeRed
													onClick={() => {
														actions.cancelOrder(order.orderId);
													}}
												>
													<XIcon width={'20px'} />
												</Button.Secondary>
											</div>
										</TableV2.BodyCell>

										{showFillButton && (
											<TableV2.BodyCell>
												<div className="w-full">
													<TableActionButton
														className="justify-start px-0"
														positive
														label="Fill"
														onClick={() => {
															actions.fillOrder({
																orderId: order.orderId,
																marketIndex: order.marketIndex,
															});
														}}
													/>
												</div>
											</TableV2.BodyCell>
										)}
									</TableV2.BodyRow>
								);
							})}

							{filteredDisplayLpOrders?.map((lpOrder, index) => {
								const marketSymbol = UIMarket.createPerpMarket(
									lpOrder.marketIndex
								).marketName;

								return (
									<TableV2.BodyRow
										key={`lp_order_${lpOrder.marketIndex}`}
										grid={tableGrid}
										isDataRow
									>
										<TableV2.MarketCell
											key={`market_${index}`}
											marketSymbol={marketSymbol}
											className="uppercase font-numeral"
											isSpot={false}
										/>

										<TableV2.BodyCell className="uppercase font-numeral">
											BAL
										</TableV2.BodyCell>

										<TableV2.BodyCell className="uppercase font-numeral">
											{showAccountValues
												? `${lpOrder.baseAmountLong
														.abs()
														.printShort(true)} LONG`
												: '∗∗∗∗∗ LONG'}{' '}
											/{' '}
											{showAccountValues
												? `${lpOrder.baseAmountShort
														.abs()
														.printShort(true)} SHORT`
												: '∗∗∗∗∗ SHORT'}
										</TableV2.BodyCell>

										<TableV2.BodyCell className="text-left uppercase font-numeral">
											-
										</TableV2.BodyCell>

										<TableV2.BodyCell className="uppercase font-numeral">
											-
										</TableV2.BodyCell>

										<TableV2.BodyCell>
											<div className="flex flex-row">
												<Button.Secondary
													className="justify-start mr-2"
													size="SMALL"
													id={`add_lp_button_${lpOrder.marketIndex}`}
													onClick={() => {
														showLpModal(
															lpOrder.marketIndex,
															'showAddLiquidityModal'
														);
													}}
												>
													<PencilIcon width={'16px'} />
												</Button.Secondary>
												<Button.Secondary
													size="SMALL"
													id={`remove_lp_button_${lpOrder.marketIndex}`}
													className={`flex items-center justify-between border-none px-1 py-1 font-display`}
													negativeRed
													onClick={() => {
														showLpModal(
															lpOrder.marketIndex,
															'showRemoveLiquidityModal'
														);
													}}
												>
													<XIcon width={'20px'} />
												</Button.Secondary>
											</div>
										</TableV2.BodyCell>

										{showFillButton && <TableV2.BodyCell>-</TableV2.BodyCell>}
									</TableV2.BodyRow>
								);
							})}
						</div>
					}
					bottom={
						<TableV2.SummaryRow
							className={'border-t border-container-border'}
							grid={''}
						>
							{props.overviewPage ? (
								<div className={`items-center w-full flex justify-end p-1`}>
									<Button.Secondary
										size="SMALL"
										disabled={
											!filteredDisplayOrders || !filteredDisplayOrders.length
										}
										onClick={actions.cancelAllOrders}
										negativeRed
									>
										{`Cancel ${filteredDisplayOrders?.length || 0} Orders`}
									</Button.Secondary>
								</div>
							) : (
								<div className={`items-center w-full flex justify-between p-1`}>
									<CheckboxInput
										checked={showOnlyCurrentMarket}
										onChange={handleToggleShowOnlyCurrentMarket}
										label="Show only selected market"
										className="text-text-default"
										secondaryStyle
									/>
									<Button.Secondary
										size="SMALL"
										disabled={
											!filteredDisplayOrders || !filteredDisplayOrders.length
										}
										onClick={handleCancelAllOrCurrentOrders}
										negativeRed
									>
										{`Cancel ${filteredDisplayOrders?.length || 0} Orders`}
									</Button.Secondary>
								</div>
							)}
						</TableV2.SummaryRow>
					}
				/>
			</TableStateWrapper>
			{showEditOrderPopup && (
				<EditOrderPopup
					targetElementId={editOrderTargetElementId}
					existingOrder={orderToEdit}
					onClose={() => setShowEditOrderPopup(false)}
				/>
			)}
		</>
	);
};

export default OpenOrdersTable;
